// src/components/resources/Breadcrumbs.tsx

'use client'

import type { ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import { motion } from 'motion/react'
import useScroll from '~/lib/use-scroll'
import { BreadcrumbTrail, type CrumbHoverWrapperProps } from '~/components/resources/BreadcrumbTrail'
import { buildResourceBreadcrumbItems } from '~/lib/resources/breadcrumbs'

/**
 * Client hover wrapper for crumb links — a `motion.div` hover-scale effect.
 *
 * Injected into the server-safe {@link BreadcrumbTrail} so `motion/react`
 * stays confined to this client shell: server-rendered trails (e.g. the
 * glossary term HydrationGate fallback) use the trail's plain-`<div>` default
 * and never pull motion into their subtree. At rest both wrappers render an
 * identical bare `<div>`, so there is no visual difference until hover.
 *
 * @param props - Wrapper props.
 * @param props.children - The crumb link to wrap.
 * @returns The motion-enhanced wrapper.
 */
function MotionCrumbHoverWrapper({ children }: CrumbHoverWrapperProps): ReactNode {
  return <motion.div whileHover={{ scale: 1.05 }}>{children}</motion.div>
}

/**
 * Props for {@link Breadcrumbs}.
 */
interface BreadcrumbsProps {
  /**
   * Optional canonical label for the final (current-page) crumb, threaded
   * from server-fetched data (glossary term / calculator / analyzer
   * canonical titles). When omitted, the label is derived from the URL
   * segment — exactly what non-data-backed pages (quizzes, generators,
   * comparisons, topics, hubs) have always shown.
   */
  leafLabel?: string
}

/**
 * Breadcrumb bar for `/resources/**` pages — thin client wrapper.
 *
 * The only genuinely client-side behavior here is the scroll-border effect
 * (`useScroll`) and the hover-scale motion wrapper; everything else — the item
 * list and every anchor tag — is derived synchronously from the pathname (plus
 * the optional server-provided `leafLabel`) and therefore renders in the
 * first-pass SSR HTML. The previous implementation built the items in a
 * `useEffect` and client-fetched term/calculator/analyzer titles that pages
 * already had server-side, so the trail carried zero anchors before hydration.
 *
 * Server and client render identical markup: `usePathname()` returns the same
 * value during SSR and hydration, and the derivation is pure.
 *
 * @param props - Component props.
 * @param props.leafLabel - Optional canonical label for the current page crumb.
 * @returns The breadcrumb bar, or `null` outside `/resources` routes.
 */
export function Breadcrumbs({ leafLabel }: BreadcrumbsProps = {}): ReactNode {
  const pathname = usePathname()
  const scrolled = useScroll(50)

  // Only show breadcrumbs on resource pages (segment-boundary check, so a
  // hypothetical `/resourcesfoo` route never renders the bar).
  if (pathname !== '/resources' && !pathname?.startsWith('/resources/')) {
    return null
  }

  const breadcrumbs = buildResourceBreadcrumbItems(pathname, leafLabel)

  return (
    <div className={`w-full transition-colors duration-200 ${
        scrolled
        ? 'border-b border-gray-200/80 dark:border-gray-800/80'
        : ''
    }`}>
      <BreadcrumbTrail items={breadcrumbs} HoverWrapper={MotionCrumbHoverWrapper} />
    </div>
  )
}
